import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { TileLayer, Popup, Polyline, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { connect } from 'react-redux';
import '@geoman-io/leaflet-geoman-free';
import { SizeMe } from 'react-sizeme';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import point from 'turf-point';
import {
  POLYGON_OPTIONS_MODAL_MODE,
  DEVICES_PAGE_TAB,
  presetPolygonColors,
  defaultDeviceMapSearchBody
} from 'common/utils/constants';
import { getMainUnitFromBundleMembers, coordinatePairToMapBoundArray, downloadJsonFile } from 'common/utils/helpers';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import {
  updateSelectedDevicesList,
  setMapSelectMode,
  searchComponentFilteredDevicesV2,
  updateDevicesPageMapViewport,
  doPolygonDuplicate,
  doPolygonCreate,
  doDeviceGroupPolygonDelete,
  setMapSize,
  setBlockedLayerShow,
  setDefaultActivePolygon,
  doPolygonUpdate,
  setActivePolygon,
  doPolygonImport,
  doPolygonExport,
  propagateFilters,
  updateDevicesPageSelectedDeviceGroupName,
  addFilter,
  loadUserPreferences,
  doGetAllUserDeviceGroups,
  doGetFactoryList,
  doGetProtectedList,
  updateDevicesPageSelectedDeviceId,
  setIsDefaultDeviceGroupInitialized
} from 'redux/actions';
import { DeviceMapStatus } from './DeviceMapSubComponents/DeviceMapStatus';
import DeviceMapOverlay from './DeviceMapSubComponents/DeviceMapOverlay/DeviceMapOverlay';
import { DeviceMapProvider } from './DeviceMapSubComponents/DeviceMapContext';
import { DeviceMarkerGroup } from './DeviceMapSubComponents/DeviceMarkerGroup';
import {
  getPolygonCoordinates,
  confirmCenterPlacingByActivePolygon,
  getPolygonShape,
  getSelectedPolygon,
  getPolygonClosestSegment,
  getDefaultPolygonColor,
  updatePolygonAttributes,
  getPolygonsByExistingPoint,
  setActivePolygonColor,
  removeAllPolygonsFromMap,
  transformLeafletCoordsToGeojson,
  createPolygonLabelContent,
  checkPolygonsNotEqual
} from './polygonUtils';
import { calculateCenterPoint, calculateDistanceBetweenTwoPoints } from './utils';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import './DeviceMap.css';

require('turf-point');

const MAX_ZOOM = 18;
const MAP_LOAD_DELAY_MS = 1000; // number of ms to wait before sending out a request when map is zoomed/dragged
const REQUEST_DEVICE_SIZE = 10000; // number of devices to request for each time

const tiles = {
  openStreetMap: {
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
  },
  googleMap: {
    attribution: '',
    url: 'http://mt0.google.com/vt/lyrs=m&hl=en&x={x}&y={y}&z={z}'
  }
};

class DeviceMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      prevSize: null,
      tileProviderName: 'openStreetMap',
      currentDrawingPolygonLayer: null,
      selectedDrawingShape: null,
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      currentIntersectionedPolygons: [],
      polygonOptionsModal: {
        isOpen: false,
        mode: POLYGON_OPTIONS_MODAL_MODE.CREATION,
        selectedPolygon: null
      },
      isAllowPolygonActivity: true,
      mapActivatedPolygonMode: null, // null | 'edition' | 'activation | 'creation' | 'gamburger-showing',
      isConfirmDeletePolygonPopUpShow: false,
      geometryColor: presetPolygonColors[0],
      currentLayerEvent: null, // null | 'click' | 'contextmenu' | 'edit'
      currentPolygonSettings: null,
      searchPolygonsText: '',
      initiallyPolygonsLoading: false,
      isMapControlPopUpShow: true,
      mapControlPopUp: {
        isOpen: false,
        autoOpen: false
      },
      selectedPosA: null,
      selectedPosB: null,
      isMeasureModeOn: false
    };

    this.loaded = false;
    this.loadDelayTimerId = null;
    this.markerRefs = [];
    this.changeTileProvider = this.changeTileProvider.bind(this);
    this.undoSavePolygon = this.undoSavePolygon.bind(this);
    this.savePolygon = this.savePolygon.bind(this);
    this.changeDrawingShape = this.changeDrawingShape.bind(this);
    this.mapSizeSwitchHandler = this.mapSizeSwitchHandler.bind(this);
    this.resizeHandler = this.resizeHandler.bind(this);
    this.handlerKeyDown = this.handlerKeyDown.bind(this);
    this.handlerPolygonClick = this.handlerPolygonClick.bind(this);
    this.selectIntersectionPolygon = this.selectIntersectionPolygon.bind(this);
    this.searchDevicesByMapPosition = this.searchDevicesByMapPosition.bind(this);
    this.setMapActivatedPolygonMode = this.setMapActivatedPolygonMode.bind(this);
  }

  get mapDevices() {
    const { mapDevices, activeTab } = this.props;

    if (activeTab === DEVICES_PAGE_TAB.pivot) {
      return mapDevices.filter((device) => device.bundleId);
    }

    return mapDevices;
  }

  // this logic does not belong to device map component
  // other components need user preferences, device groups, protected and factory device lists
  // it's here just because map component should initialize before data will be loaded
  load() {
    if (this.loaded) {
      return;
    }

    const { dispatch } = this.props;

    dispatch(loadUserPreferences());
    dispatch(doGetAllUserDeviceGroups(this.handleDeviceGroupSuccess, this.handleDeviceGroupError));
    dispatch(doGetFactoryList());
    dispatch(doGetProtectedList());

    this.loaded = true;
  }

  handleDeviceGroupSuccess = (deviceGroups) => {
    const { dispatch, activeTab, selectedDeviceGroupName, isDefaultDeviceGroupInitialized } = this.props;
    const defaultDeviceGroup = Object.values(deviceGroups).find((deviceGroup) => deviceGroup.default);

    if (!selectedDeviceGroupName && !defaultDeviceGroup) {
      dispatch(updateDevicesPageSelectedDeviceGroupName(null));
      return;
    }

    if (!selectedDeviceGroupName && !isDefaultDeviceGroupInitialized && defaultDeviceGroup) {
      const { name, devices } = defaultDeviceGroup || {};

      dispatch(updateDevicesPageSelectedDeviceGroupName(name));
      dispatch(addFilter(activeTab, { deviceId: devices }, true));
      dispatch(setIsDefaultDeviceGroupInitialized(true));
    }
  };

  handleDeviceGroupError = () => {
    const { dispatch } = this.props;

    dispatch(updateDevicesPageSelectedDeviceGroupName(null));
  };

  polygonListenerRegistration(layer) {
    layer.on('contextmenu', (e) => {
      this.setState({ currentLayerEvent: 'contextmenu' });
      const selectedPolygons = getPolygonsByExistingPoint(this.map.leafletElement, e.latlng);

      if (selectedPolygons.length === 1) {
        this.setState({
          polygonOptionsModal: {
            isOpen: true,
            mode: POLYGON_OPTIONS_MODAL_MODE.UPDATION,
            selectedPolygon: getSelectedPolygon(this.props.polygonList, e.target.deviceGroupId)
          }
        });
      } else {
        this.setState({
          currentIntersectionedPolygons: selectedPolygons
        });
      }
    });
  }

  polygonClickListenerRegistration(layer) {
    layer.on('click', (e) => {
      if (
        !this.map.leafletElement.pm.globalDrawModeEnabled() &&
        this.state.currentIntersectionedPolygons.length === 0
      ) {
        this.setState({ currentLayerEvent: 'click' });
        this.handlerPolygonClick(e, layer);
        L.DomEvent.stopPropagation(e);

        this.props.dispatch(setBlockedLayerShow(true));
        setTimeout(() => this.props.dispatch(setBlockedLayerShow(false)), 400);
      }
    });
  }

  polygonDoubleClickListenerRegistration(layer) {
    layer.on('dblclick', (e) => {
      this.map.leafletElement.doubleClickZoom.disable();
      L.DomEvent.stopPropagation(e);
    });
  }

  stopPolygonEditingShape = () => {
    this.map.leafletElement.pm.disableGlobalEditMode();
  };

  polygonEditListenerRegistration(layer) {
    layer.on('pm:update', (e) => {
      const currentPolygon = getSelectedPolygon(this.props.polygonList, layer.deviceGroupId);

      if (!currentPolygon) {
        this.map.leafletElement.removeLayer(layer);
        return;
      }

      const payload = {
        properties: {
          ...currentPolygon.properties,
          name: layer.name,
          fill: layer.options.color,
          stroke: layer.options.color,
          default:
            this.state.currentPolygonSettings !== null
              ? this.state.currentPolygonSettings.default
              : currentPolygon.properties.default
        },
        geometry: {
          type: 'Polygon',
          coordinates: transformLeafletCoordsToGeojson(layer)
        }
      };

      this.setState({ currentPolygonSettings: null });
      this.props.dispatch(
        doPolygonUpdate(layer.deviceGroupId, payload, () => {
          this.pivotsFiltering();
        })
      );
    });

    layer.on('pm:disable', (e) => {
      layer.openTooltip();
      this.setState((prevState) => ({
        isAllowPolygonActivity: true,
        mapControlPopUp: { isOpen: prevState.mapControlPopUp.autoOpen, autoOpen: false }
      }));
    });
  }

  setMapActivatedPolygonMode = (mode) => {
    this.setState({ mapActivatedPolygonMode: mode });
  };

  setSearchPolygonsText = (searchText) => {
    this.setState({ searchPolygonsText: searchText });
  };

  searchDevicesByMapPosition(stopFilteringByActivePolygon = false) {
    let bounds = this.map?.leafletElement?.getBounds();
    if (!bounds) return;

    const loadTimerId = setTimeout(() => {
      if (!this.loadDelayTimerId) return;
      let boundArray = coordinatePairToMapBoundArray(
        bounds._northEast.lat,
        bounds._northEast.lng,
        bounds._southWest.lat,
        bounds._southWest.lng
      );

      //@todo throw in other filters
      let filters = {
        polygon: boundArray
      };

      if (this.props.selectedDeviceGroupName && this.props.selectedDeviceGroups[this.props.selectedDeviceGroupName]) {
        filters.deviceId = this.props.selectedDeviceGroups[this.props.selectedDeviceGroupName].devices;
      }

      let newFilterDictionary = { ...this.props.filterDictionary, ...filters };

      // Reading pivots methods are initialed
      if (this.props.activePolygonId || stopFilteringByActivePolygon) {
        this.props.dispatch(propagateFilters(this.props.activeTab, newFilterDictionary));
      } else {
        this.props.dispatch(
          searchComponentFilteredDevicesV2(
            this.props.activeTab,
            'deviceMap',
            { ...defaultDeviceMapSearchBody, size: REQUEST_DEVICE_SIZE },
            newFilterDictionary
          )
        );
      }
    }, MAP_LOAD_DELAY_MS);

    if (this.loadDelayTimerId) clearTimeout(this.loadDelayTimerId);
    this.loadDelayTimerId = loadTimerId;
  }

  changeTileProvider(tileProviderName) {
    this.setState({ tileProviderName });
  }

  enablePolygonEditing = (layer) => {
    layer.closeTooltip();
    layer.pm.enable({
      allowSelfIntersection: false,
      snappable: false,
      removeVertexValidation: (event) => {
        const selectedPolygon = getSelectedPolygon(this.props.polygonList, event.layer.deviceGroupId);
        this.props.dispatch(setActivePolygon(selectedPolygon.deviceGroupId));

        if (event.layer._latlngs[0].length === 3) {
          this.setState((prevState) => ({
            polygonOptionsModal: {
              ...prevState.polygonOptionsModal,
              selectedPolygon,
              mapControlPopUp: { isOpen: false, autoOpen: prevState.mapControlPopUp.isOpen }
            },
            isConfirmDeletePolygonPopUpShow: true
          }));
          return false;
        }
        return true;
      }
    });

    this.setState({ isAllowPolygonActivity: false });
  };

  handlerPolygonClick(e, layer) {
    this.map.leafletElement.pm.disableGlobalEditMode();
    const selectedPolygons = getPolygonsByExistingPoint(this.map.leafletElement, e.latlng); // How many polygons hover each other

    if (selectedPolygons.length === 1) {
      const closestPolygonBorder = getPolygonClosestSegment(e.latlng, layer);
      // One polygon is clicked.
      if (closestPolygonBorder.pointToLineDistance <= 8) {
        // - Start editing of polygon
        this.enablePolygonEditing(layer);
        this.setState(
          (prevState) => ({
            mapActivatedPolygonMode: 'edition',
            mapControlPopUp: { isOpen: false, autoOpen: prevState.mapControlPopUp.isOpen }
          }),
          () => {
            if (this.props.activePolygonId !== layer.deviceGroupId) {
              this.props.dispatch(setActivePolygon(layer.deviceGroupId));
            }
          }
        );
      } else {
        // - Activation selected polygon
        this.setState({ mapActivatedPolygonMode: 'activation' }, () => {
          this.props.dispatch(setActivePolygon(layer.deviceGroupId));
        });
      }
    } else {
      const borderClickedPolygons = selectedPolygons.filter(
        (polygon) => getPolygonClosestSegment(e.latlng, polygon).pointToLineDistance <= 8
      );

      // Several polygons are clicked
      if (borderClickedPolygons.length) {
        // - Start editing of polygons
        if (borderClickedPolygons.length === 1) {
          this.onMapControlPopUpClickHandler(false, this.state.mapControlPopUp.isOpen);
          this.enablePolygonEditing(borderClickedPolygons[0]);
        } else {
          this.setState({ mapActivatedPolygonMode: 'edition', currentIntersectionedPolygons: borderClickedPolygons });
        }
      } else {
        // Activation / deactivation mode
        this.setState({ mapActivatedPolygonMode: 'activation', currentIntersectionedPolygons: selectedPolygons });
      }
    }
  }

  startDrawGeometry = (geometry) => {
    const geometryColor = getDefaultPolygonColor();
    this.setState({
      geometryColor,
      mapActivatedPolygonMode: 'creation',
      mapControlPopUp: { isOpen: false, autoOpen: true }
    });

    this.map.leafletElement.pm.disableGlobalEditMode();
    this.map.leafletElement.pm.enableDraw(geometry, {
      snappable: false,
      tooltips: false,
      templineStyle: { color: geometryColor },
      hintlineStyle: { color: geometryColor, dashArray: [5, 5] },
      pathOptions: { color: geometryColor, fill: false }
    });
  };

  selectIntersectionPolygon(layer) {
    if (this.state.currentLayerEvent === 'click' && this.state.mapActivatedPolygonMode === 'activation') {
      this.props.dispatch(setActivePolygon(layer.deviceGroupId));
      this.setState({
        currentIntersectionedPolygons: [],
        isAllowPolygonActivity: true
      });
    }

    if (this.state.currentLayerEvent === 'click' && this.state.mapActivatedPolygonMode === 'edition') {
      this.props.dispatch(setActivePolygon(layer.deviceGroupId));
      this.enablePolygonEditing(layer);
      this.setState({
        currentIntersectionedPolygons: [],
        isAllowPolygonActivity: true
      });
    }

    if (this.state.currentLayerEvent === 'contextmenu') {
      this.setState({
        currentDrawingPolygonLayer: null,
        polygonOptionsModal: {
          isOpen: true,
          mode: POLYGON_OPTIONS_MODAL_MODE.UPDATION,
          selectedPolygon: getSelectedPolygon(this.props.polygonList, layer.deviceGroupId)
        },
        currentIntersectionedPolygons: []
      });
    }
  }

  changeDrawingShape(currentShape, geometry) {
    this.stopPolygonEditingShape();

    this.map.leafletElement.pm.disableDraw();
    if (currentShape === this.state.selectedDrawingShape) {
      this.setState({ selectedDrawingShape: null });
    } else {
      this.setState({ selectedDrawingShape: currentShape });
      this.startDrawGeometry(geometry);
    }
  }

  undoSavePolygon() {
    this.map.leafletElement.pm.disableDraw();

    if (this.state.currentDrawingPolygonLayer) {
      this.map.leafletElement.removeLayer(this.state.currentDrawingPolygonLayer);
    }

    this.setState((prevState) => ({
      currentDrawingPolygonLayer: null,
      selectedDrawingShape: null,
      isAllowPolygonActivity: true,
      mapControlPopUp: {
        isOpen: prevState.mapControlPopUp.autoOpen,
        autoOpen: false
      }
    }));

    if (this.props.activePolygonId) {
      // Return to the previous activation process
      this.setState({ mapActivatedPolygonMode: 'activation' });
    }
  }

  savePolygon(mode, newPolygonSettings, selectedPolygon = null) {
    const currentPolygonSettings = { ...newPolygonSettings, name: newPolygonSettings.name.trim() };

    // Update created polygon attributes
    if (mode === POLYGON_OPTIONS_MODAL_MODE.UPDATION) {
      const polygonId = selectedPolygon.deviceGroupId;
      this.setState({ currentPolygonSettings: newPolygonSettings });

      const currentPolygon = getSelectedPolygon(this.props.polygonList, polygonId);
      const payload = {
        geometry: {
          ...currentPolygon.geometry
        },
        properties: {
          ...currentPolygon.properties,
          ...currentPolygonSettings,
          stroke: currentPolygonSettings.fill
        }
      };

      this.props.dispatch(
        doPolygonUpdate(polygonId, payload, (result) => {
          updatePolygonAttributes(this.map, polygonId, currentPolygonSettings);

          this.setState(
            (prevState) => ({
              ...prevState,
              isAllowPolygonActivity: true,
              polygonOptionsModal: { isOpen: false }
            }),
            () => {
              this.props.dispatch(setActivePolygon(polygonId));
            }
          );

          this.map.leafletElement.pm.disableGlobalEditMode();
        })
      );

      return;
    }

    // Create new polygon || duplicate existing polygon
    if (mode === POLYGON_OPTIONS_MODAL_MODE.CREATION || mode === POLYGON_OPTIONS_MODAL_MODE.DUPLICATION) {
      const polygonPayload = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates:
            mode === POLYGON_OPTIONS_MODAL_MODE.CREATION
              ? transformLeafletCoordsToGeojson(this.state.currentDrawingPolygonLayer)
              : selectedPolygon.geometry.coordinates
        },
        properties: {
          ...currentPolygonSettings,
          visible: true,
          stroke: currentPolygonSettings.fill,
          fillOpacity: 0.12
        }
      };

      this.props.dispatch(
        doPolygonCreate('geo', polygonPayload, (result) => {
          this.setState((prevState) => ({
            ...prevState,
            polygonOptionsModal: { isOpen: false },
            selectedDrawingShape: null,
            isAllowPolygonActivity: true,
            currentDrawingPolygonLayer: {
              ...prevState.currentDrawingPolygonLayer,
              deviceGroupId: result.polygon.deviceGroupId
            },
            searchPolygonsText: '',
            mapActivatedPolygonMode: null
          }));
        })
      );
    }

    if (mode === POLYGON_OPTIONS_MODAL_MODE.CREATION) {
      this.onMapControlPopUpClickHandler(this.state.mapControlPopUp.autoOpen);
    }
  }

  deletePolygon = (deviceGroupId) => {
    this.props.dispatch(
      doDeviceGroupPolygonDelete(deviceGroupId, () => {
        this.setState((prevState) => ({
          polygonOptionsModal: { isOpen: false },
          isConfirmDeletePolygonPopUpShow: false,
          mapControlPopUp: { isOpen: prevState.mapControlPopUp.isOpen, autoOpen: false }
        }));

        this.map.leafletElement.eachLayer((layer) => {
          if (layer instanceof L.Polygon) {
            if (layer.deviceGroupId === deviceGroupId) {
              this.map.leafletElement.removeLayer(layer);
            }
          }
        });

        // Remove filtering by polygon
        this.props.dispatch(setActivePolygon(null));
      })
    );
  };

  importPolygon = (data) => {
    this.props.dispatch(
      doPolygonImport(data, (result) => {
        const layer = getPolygonShape(result.polygon.geometry.coordinates);
        layer.deviceGroupId = result.polygon.deviceGroupId;

        this.setState({ currentDrawingPolygonLayer: layer });
      })
    );
  };

  exportPolygon = (deviceGroupId) => {
    this.props.dispatch(
      doPolygonExport(deviceGroupId, (result) => {
        const selectedPolygon = getSelectedPolygon(this.props.polygonList, deviceGroupId);
        downloadJsonFile(result.polygon, selectedPolygon.properties.name);
        this.setState({
          polygonOptionsModal: { isOpen: false }
        });
      })
    );
  };

  pivotsFiltering() {
    if (this.props.isDefaultPolygonInitialised) {
      return;
    }

    const stopFilteringByActivePolygon = !this.props.activePolygonId;
    this.searchDevicesByMapPosition(stopFilteringByActivePolygon);
  }

  toggleDeleteConfirmationModal = (isOpen) => this.setState({ isConfirmDeletePolygonPopUpShow: isOpen });

  resizeHandler() {
    this.setState({ screenSize: { width: window.innerWidth, height: window.innerHeight } });
  }

  handlerKeyDown(e) {
    // Drawing polygon in a process. Escape from this
    if (this.state.selectedDrawingShape && e.keyCode === 27) {
      this.undoSavePolygon();
    }
  }

  mapSizeSwitchHandler(status) {
    setTimeout(() => this.map.leafletElement.invalidateSize(), 400);
    this.props.dispatch(setMapSize(status));
    this.resizeHandler();
  }

  measureDistanceHandler = () => {
    const { isMeasureModeOn } = this.state;
    const { selectedDeviceId, dispatch } = this.props;

    if (isMeasureModeOn) {
      this.setState({ selectedPosA: null, selectedPosB: null, isMeasureModeOn: false });
    } else {
      this.setState({ isMeasureModeOn: true });
    }
    selectedDeviceId && dispatch(updateDevicesPageSelectedDeviceId(null));
  };

  handleMeasureDeviceOnClick = (position) => {
    const { selectedPosA, selectedPosB } = this.state;
    const newPosition = [position?.lat || 0, position?.lng || 0];
    if (!selectedPosA) {
      this.setState({ selectedPosA: newPosition });
    } else if (!selectedPosB) {
      this.setState({ selectedPosB: newPosition });
    } else {
      this.setState({ selectedPosA: newPosition, selectedPosB: null });
    }
  };

  updateCenterPositionAndZoomLevel = () => {
    const { selectedPosA, selectedPosB } = this.state;

    if (!selectedPosA || !selectedPosB || !this.map?.leafletElement) return;

    const bounds = L.latLngBounds(selectedPosA, selectedPosB);
    const zoom = this.map.leafletElement.getBoundsZoom(bounds);
    const center = calculateCenterPoint(selectedPosA, selectedPosB);
    const viewport = { center, zoom };

    this.props.dispatch(updateDevicesPageMapViewport(viewport));
  };

  // Get event from MapControlPopUp component
  handlerShowPolygonOptionsModal = (selectedPolygon, mode) => {
    if (mode === POLYGON_OPTIONS_MODAL_MODE.DUPLICATION) {
      this.props.dispatch(
        doPolygonDuplicate(selectedPolygon._id, (newDuplicatePolygonName) => {
          this.setState({
            polygonOptionsModal: {
              isOpen: true,
              mode: mode,
              selectedPolygon: {
                ...selectedPolygon,
                properties: { ...selectedPolygon.properties, name: newDuplicatePolygonName }
              }
            }
          });
        })
      );
    } else {
      this.setState({
        polygonOptionsModal: {
          isOpen: true,
          mode: mode,
          selectedPolygon
        }
      });
    }
  };

  closePolygonOptionsModal = () => {
    if (this.state.polygonOptionsModal.mode === POLYGON_OPTIONS_MODAL_MODE.CREATION) {
      this.undoSavePolygon();
    }
    this.setState({ polygonOptionsModal: { isOpen: false } });
  };

  onMapControlPopUpClickHandler = (isOpen, autoOpen = false) => {
    this.setState({ mapControlPopUp: { isOpen, autoOpen } });
    this.stopPolygonEditingShape();
  };

  componentDidMount() {
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('keydown', this.handlerKeyDown);

    this.setState({ initiallyPolygonsLoading: true });

    const map = this.map.leafletElement;

    map.zoomControl.remove();

    map.pm.addControls({
      position: 'topleft',
      drawCircle: false,
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false
    });

    map.pm.toggleControls(false);

    map.pm.setGlobalOptions({
      allowSelfIntersection: false
    });

    // Map click event
    map.on('click', (e) => {
      this.map.leafletElement.doubleClickZoom.enable();

      if (!this.map.leafletElement.pm.globalDrawModeEnabled()) {
        this.map.leafletElement.pm.disableGlobalEditMode();
      }
      this.setState({ currentIntersectionedPolygons: [] });

      if (!this.state.mapActivatedPolygonMode && this.props.activePolygonId) {
        this.props.dispatch(setActivePolygon(null));
      }

      if (this.state.mapActivatedPolygonMode === 'activation' || this.state.mapActivatedPolygonMode === 'edition') {
        this.props.dispatch(setActivePolygon(null));
        this.setState({ mapActivatedPolygonMode: null });
      }

      if (this.state.mapActivatedPolygonMode === 'gamburger-showing') {
        this.setState({ mapActivatedPolygonMode: 'activation' });
      }
    });

    map.on('pm:drawstart', ({ workingLayer }) => {
      this.setState({ currentDrawingPolygonLayer: null });

      workingLayer.on('pm:vertexadded', (e) => {
        this.setState({ isAllowPolygonActivity: false });
      });
    });

    map.on('pm:create', (e) => {
      this.setState({ polygonOptionsModal: { isOpen: true, mode: POLYGON_OPTIONS_MODAL_MODE.CREATION } });
      const layer = e.layer;
      layer.setStyle({ color: this.state.geometryColor, fillColor: 'transparent' });
      this.setState({ currentDrawingPolygonLayer: layer });

      let selectedDevices = { ...this.props.selectedDevicesDictionary };
      this.mapDevices.forEach((device) => {
        let isBundle = isItBundle(device);
        let mainDevice = isBundle ? getMainUnitFromBundleMembers(device.members) : device;
        let selectedId = isBundle ? device.bundleId : device.deviceId;
        try {
          if (
            (this.props.mapSelectMode.shape === 'rectangle' &&
              layer.getBounds().contains({
                lat: mainDevice.properties?.reported?.location?.lat,
                lng: mainDevice.properties?.reported?.location?.long
              })) ||
            (this.props.mapSelectMode.shape === 'circle' &&
              layer.getLatLng().distanceTo({
                lat: mainDevice.properties?.reported?.location?.lat,
                lng: mainDevice.properties?.reported?.location?.long
              }) < layer.getRadius()) ||
            (this.props.mapSelectMode.shape === 'polygon' &&
              booleanPointInPolygon(
                point([
                  mainDevice.properties?.reported?.location?.long,
                  mainDevice.properties?.reported?.location?.lat
                ]),
                layer.toGeoJSON()
              ))
          ) {
            selectedDevices[selectedId] = true;
          }
        } catch (e) {}
      });
      this.props.dispatch(setMapSelectMode({ shape: 'none' }));
      this.props.dispatch(updateSelectedDevicesList(this.props.activeTab, selectedDevices));
    });

    /* When the map gets resized, the leaflet element needs to be explicitly told to recalulate what map panels need to be loaded */
    if (this.state.prevSize == null) {
      this.setState({ prevSize: this.props.mapListViewSwitchState });
    } else if (this.state.prevSize !== this.props.mapListViewSwitchState) {
      const map = this.map.leafletElement;
      //invalidate with animate set to false so no panning takes place on resize.
      map.invalidateSize(false);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    /* When the map gets resized, the leaflet element needs to be explicitly told to recalulate what map panels need to be loaded */
    if (this.state.prevSize == null) {
      this.setState({ prevSize: this.props.mapListViewSwitchState });
    } else if (this.state.prevSize !== this.props.mapListViewSwitchState) {
      const map = this.map.leafletElement;
      map.invalidateSize(false); //invalidate with animate set to false so no panning takes place on resize.
    }

    // Add existing polygons
    // Add the layers to the drawnItems feature group
    if (checkPolygonsNotEqual(prevProps.polygonList, this.props.polygonList) || this.state.initiallyPolygonsLoading) {
      removeAllPolygonsFromMap(this.map.leafletElement);

      const reversedPolygonList = [...this.props.polygonList].reverse();

      reversedPolygonList.forEach((polygon) => {
        const layer = getPolygonShape(polygon.geometry.coordinates);

        // add event handlers
        this.polygonListenerRegistration(layer);
        this.polygonClickListenerRegistration(layer);
        this.polygonDoubleClickListenerRegistration(layer);
        this.polygonEditListenerRegistration(layer);

        layer.name = polygon.properties.name;
        layer.deviceGroupId = polygon.deviceGroupId;

        layer.setStyle({
          fillColor: 'transparent',
          fillOpacity: polygon.properties.fillOpacity,
          color: polygon.properties.stroke
        });

        const polygonLabel = new L.Tooltip({
          className: 'polygon-label',
          permanent: true,
          direction: 'center'
        });

        polygonLabel.setContent(createPolygonLabelContent(polygon.properties.name, polygon.properties.fill));
        layer.bindTooltip(polygonLabel);

        layer.addTo(this.map.leafletElement);
      });

      // Created polygon activates
      if (this.state.currentDrawingPolygonLayer) {
        const currentDeviceGroupId = this.state.currentDrawingPolygonLayer.deviceGroupId;

        if (currentDeviceGroupId) {
          this.props.dispatch(setActivePolygon(currentDeviceGroupId));
        }
      }

      this.setState({
        currentDrawingPolygonLayer: null,
        initiallyPolygonsLoading: false
      });
    }

    // If polygon activates event is occured
    if (prevProps.activePolygonId !== this.props.activePolygonId) {
      this.map.leafletElement.pm.disableDraw();
      this.stopPolygonEditingShape();
      this.setState({ selectedDrawingShape: null });

      const deviceGroupIdCurrent = this.props.activePolygonId;
      setActivePolygonColor(this.map, deviceGroupIdCurrent);
      this.pivotsFiltering();
      if (deviceGroupIdCurrent) {
        const polygonCurrent = this.props.polygonList.find((polygon) => polygon.deviceGroupId === deviceGroupIdCurrent);

        if (polygonCurrent) {
          if (confirmCenterPlacingByActivePolygon(this.map, polygonCurrent) || this.props.isDefaultPolygonInitialised) {
            const coordinates = getPolygonCoordinates(polygonCurrent);
            this.map.leafletElement.fitBounds(coordinates);
          }
        }
      }
    }

    this.load();

    if (prevProps.selectedDeviceId !== this.props.selectedDeviceId) {
      try {
        if (this.map) {
          let devices = this.mapDevices;
          let mainDevice = {}; // can only be device, for finding DU's location
          // let isFoundOnMap = false;

          for (let device of devices) {
            let isBundle = isItBundle(device);
            let selectedId = isBundle ? device.bundleId : device.deviceId;
            if (selectedId && selectedId === this.props.selectedDeviceId) {
              // isFoundOnMap = true;
              mainDevice = isBundle ? getMainUnitFromBundleMembers(device.members) : device; //either device itself or the DU from bundle
              break;
            }
          }
          // if (isFoundOnMap === false) {
          //   this.props.dispatch(updateDeviceSets('deviceTable', [this.props.selectedDeviceId]));
          // }
          let location = mainDevice && mainDevice.properties?.reported?.location;
          let marker =
            location &&
            this.markerRefs &&
            this.markerRefs.find((marker) => {
              const deviceMarkerId = marker.props?.device?.tags?.bundle?.bundleId ?? marker.props?.device?.deviceId;

              return deviceMarkerId === this.props.selectedDeviceId;
            });
          let latlng = location && marker && { lat: location && location.lat, lng: location && location.long };

          if (
            location &&
            marker &&
            latlng &&
            !(
              this.map.leafletElement.getBounds().contains(latlng) &&
              this.map.leafletElement.hasLayer(marker.leafletElement)
            )
          ) {
            this.map.leafletElement.setView(latlng, 15, { duration: 0.5 });
          }
        }
      } catch (error) {
        console.log('map select error', error);
      }
    }

    if (
      this.state.selectedPosA &&
      this.state.selectedPosB &&
      (prevState.selectedPosA !== this.state.selectedPosA || prevState.selectedPosB !== this.state.selectedPosB)
    ) {
      this.updateCenterPositionAndZoomLevel();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('keydown', this.handlerKeyDown);

    if (this.loadDelayTimerId) {
      clearTimeout(this.loadDelayTimerId);
      this.loadDelayTimerId = null;
    }
  }

  zoomHandle = (direction) => {
    const map = this.map.leafletElement;

    if (direction === 'in') {
      map.zoomIn();
    }

    if (direction === 'out') {
      map.zoomOut();
    }
  };

  handleMoveEnd = () => {
    if (this.props.isDefaultPolygonInitialised) {
      const deviceGroupIdCurrent = this.props.activePolygonId;
      this.props.dispatch(setDefaultActivePolygon(deviceGroupIdCurrent, false));
    } else {
      this.searchDevicesByMapPosition();
    }
  };

  handleMoveStart = () => {
    if (this.loadDelayTimerId) {
      clearTimeout(this.loadDelayTimerId);
      this.loadDelayTimerId = null;
    }
  };

  handleViewportChange = (viewport) => {
    const { dispatch, zoom, location } = this.props;
    const latChanged = viewport?.center?.[0] !== location?.[0];
    const lngChanged = viewport?.center?.[1] !== location?.[1];
    const centerChanged = latChanged || lngChanged;
    const zoomChanged = zoom !== viewport?.zoom;

    if (centerChanged || zoomChanged) {
      dispatch(updateDevicesPageMapViewport(viewport));
    }
  };

  registerMap = (map) => {
    this.map = map;
  };

  registerMarker = (marker, index) => {
    this.markerRefs[index] = marker;
  };

  render() {
    const { zoom, location, mapSelectMode, selectedDeviceId, selectedDevicesDictionary } = this.props;
    const { selectedPosA, selectedPosB, isMeasureModeOn } = this.state;
    const maxZoom = MAX_ZOOM;

    return (
      <div className={`DeviceMap ${this.props.mapSize}`}>
        <DeviceMapStatus />
        <DeviceMapProvider
          ref={this.registerMap}
          style={{ height: '100%' }}
          center={location}
          zoom={zoom}
          maxZoom={maxZoom}
          ondragstart={this.handleMoveStart}
          ondragend={this.handleMoveEnd}
          onzoomstart={this.handleMoveStart}
          onzoomend={this.handleMoveEnd}
          onViewportChanged={this.handleViewportChange}
          pmIgnore={false}
        >
          <TileLayer
            attribution={tiles[this.state.tileProviderName].attribution}
            url={tiles[this.state.tileProviderName].url}
            noWrap={true}
          />
          <DeviceMarkerGroup
            devices={this.mapDevices}
            mapSelectMode={mapSelectMode}
            selectedDeviceId={selectedDeviceId}
            selectedDevicesDictionary={selectedDevicesDictionary}
            maxZoom={maxZoom}
            register={this.registerMarker}
            handleMeasureDeviceOnClick={this.handleMeasureDeviceOnClick}
            isMeasureModeOn={isMeasureModeOn}
          />
          {selectedPosA?.length === 2 && selectedPosB?.length === 2 && (
            <>
              <Polyline positions={[selectedPosA, selectedPosB]} fillColor={'#005082'} color={'#005082'}>
                <Tooltip sticky>sticky Tooltip for Polygon</Tooltip>
              </Polyline>

              <Popup position={selectedPosA}>
                Distance: {calculateDistanceBetweenTwoPoints(selectedPosA, selectedPosB)} meters
              </Popup>
            </>
          )}
        </DeviceMapProvider>
        <SizeMe>
          {({ size }) => (
            <DeviceMapOverlay
              activeTab={this.props.activeTab}
              zoomHandler={this.zoomHandle}
              mapSelectMode={mapSelectMode}
              size={size}
              tileProviderName={this.state.tileProviderName}
              changeTileProvider={this.changeTileProvider}
              stopPolygonEditingShape={this.stopPolygonEditingShape}
              polygonOptionsModal={this.state.polygonOptionsModal}
              handlerShowPolygonOptionsModal={this.handlerShowPolygonOptionsModal}
              savePolygon={this.savePolygon}
              selectedDrawingShape={this.state.selectedDrawingShape}
              changeDrawingShape={this.changeDrawingShape}
              polygonList={this.props.polygonList}
              closePolygonOptionsModal={this.closePolygonOptionsModal}
              deletePolygon={this.deletePolygon}
              mapSizeSwitchHandler={this.mapSizeSwitchHandler}
              screenSize={this.state.screenSize}
              currentIntersectionedPolygons={this.state.currentIntersectionedPolygons}
              selectIntersectionPolygon={this.selectIntersectionPolygon}
              isAllowPolygonActivity={this.state.isAllowPolygonActivity}
              isConfirmDeletePolygonPopUpShow={this.state.isConfirmDeletePolygonPopUpShow}
              toggleDeleteConfirmationModal={this.toggleDeleteConfirmationModal}
              geometryColor={this.state.geometryColor}
              searchPolygonsText={this.state.searchPolygonsText}
              setSearchPolygonsText={this.setSearchPolygonsText}
              importPolygon={this.importPolygon}
              exportPolygon={this.exportPolygon}
              activePolygonId={this.props.activePolygonId}
              setMapActivatedPolygonMode={this.setMapActivatedPolygonMode}
              mapControlPopUp={this.state.mapControlPopUp}
              onMapControlPopUpClickHandler={this.onMapControlPopUpClickHandler}
              measureDistanceHandler={this.measureDistanceHandler}
              isMeasureModeOn={this.state.isMeasureModeOn}
            />
          )}
        </SizeMe>
      </div>
    );
  }
}

export default withRouter(
  connect((store) => {
    let mapDevices = [];
    let filterDictionary = {};
    let selectedDevicesDictionary;
    let mapListViewSwitchState;
    let mapSelectMode;

    const activeTab = store.ui.devicesPage.activeTab;
    if (activeTab === DEVICES_PAGE_TAB.pivot) {
      mapDevices = store.data.pivotDeviceSets?.deviceMap?.devices;
      filterDictionary = store.data.pivotFilterDictionary;
      selectedDevicesDictionary = store.ui.devicesPage.pivotTab.selectedDevicesDictionary;
      mapListViewSwitchState = store.ui.devicesPage.pivotTab.mapListViewSwitchState;
      mapSelectMode = store.ui.devicesPage.pivotTab.selectMode;
    } else if (activeTab === DEVICES_PAGE_TAB.echo) {
      mapDevices = store.data.echoDeviceSets?.deviceMap?.devices;
      filterDictionary = store.data.echoFilterDictionary;
      selectedDevicesDictionary = store.ui.devicesPage.echoTab.selectedDevicesDictionary;
      mapListViewSwitchState = store.ui.devicesPage.echoTab.mapListViewSwitchState;
      mapSelectMode = store.ui.devicesPage.echoTab.selectMode;
    } else {
      mapDevices = store.data.allDeviceSets?.deviceMap?.devices;
      filterDictionary = store.data.allDevicesFilterDictionary;
      selectedDevicesDictionary = store.ui.devicesPage.allDevicesTab.selectedDevicesDictionary;
      mapListViewSwitchState = store.ui.devicesPage.allDevicesTab.mapListViewSwitchState;
      mapSelectMode = store.ui.devicesPage.allDevicesTab.selectMode;
    }

    return {
      activeTab,
      mapDevices,
      filterDictionary,
      mapListViewSwitchState,
      mapSelectMode,
      location: store.ui.devicesPage.mapLocation,
      zoom: store.ui.devicesPage.mapZoom,
      selectedDeviceId: store.ui.devicesPage.selectedDeviceId,
      selectedDevicesDictionary,
      lastSelection: store.ui.devicesPage.lastSelection,
      selectedDeviceGroupName: store.ui.devicesPage.selectedDeviceGroupName,
      isDefaultDeviceGroupInitialized: store.ui.devicesPage.isDefaultDeviceGroupInitialized,
      selectedDeviceGroups: store.ui.devicesPage.deviceGroups,
      polygonList: store.ui.devicesPage.polygons,
      activePolygonId: store.ui.devicesPage.activePolygonId,
      isDefaultPolygonInitialised: store.ui.devicesPage.isDefaultPolygonInitialised,
      mapSize: store.style.mapSize
    };
  })(DeviceMap)
);
